// 인증·역할 기반 라우트 보호 컴포넌트
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import type { UserRole } from '@/types/auth'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: UserRole[]
}

export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, userProfile, isLoading, isProfileLoading } = useAuth()
  const location = useLocation()

  if (isLoading || isProfileLoading) {
    return (
      <div className="min-h-screen bg-canvly-warm flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-5 h-5 border-2 border-canvly-gold border-t-transparent rounded-full animate-spin" />
          <p className="text-canvly-stone text-sm">로딩 중...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  if (user && !userProfile?.gallery_id && userProfile?.role !== 'super_admin') {
    return <Navigate to="/onboarding" replace />
  }

  // 슈퍼어드민은 갤러리 페이지(/dashboard, /calendar 등)에 들어오면 슈퍼어드민 페이지로 리다이렉트
  // 단 /super-admin/* 경로와 명시적으로 super_admin role 이 허용된 페이지는 통과
  if (userProfile?.role === 'super_admin' && !location.pathname.startsWith('/super-admin')) {
    const isExplicitlyAllowed = allowedRoles?.includes('super_admin')
    if (!isExplicitlyAllowed) {
      return <Navigate to="/super-admin" replace />
    }
  }

  if (allowedRoles && userProfile && !allowedRoles.includes(userProfile.role)) {
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}
